'use client';

import { ImageRatio } from '@diritto/common/base/constants';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ZIndex } from 'constants/zIndex';
import Link from 'next/link';
import React from 'react';
import { getCoverImageBorder, getCoverImageStyle, getTextLineStyle, hideScrollbar } from 'styles/common';
import { IkImage } from 'ui-components/Imagekit/IkImage';

export function ExploreContentList({ contents, displayCount }: { contents: ContentDto[]; displayCount: number }) {
  return (
    <StyledContentCoverWrapper>
      {contents.slice(0, displayCount).map(content => {
        const size = 50;
        const imageSize = 114;

        return (
          <StyledContentWrapper key={content.id}>
            <Link href={`/contents/${content.id}`}>
              <StyledCoverWrapper canEnlarge={false} ratio={ImageRatio} size={size}>
                <IkImage
                  transform={{
                    path: content.cover.path,
                    isPrivate: content.cover.isPrivate,
                    transformation: imageSize
                      ? { ...ImageTransformOptions[ImageTransformType.ContentCover], width: imageSize }
                      : ImageTransformOptions[ImageTransformType.ContentCover],
                  }}
                  alt={`${content.title} 표지`}
                />
              </StyledCoverWrapper>
            </Link>
            <StyledContentMetaContainer>
              <Link href={`/contents/${content.id}`}>
                <StyledTitle>{content.title}</StyledTitle>
              </Link>
              <StyledMeta>
                <StyledDescription>{content.description}</StyledDescription>
              </StyledMeta>
            </StyledContentMetaContainer>
          </StyledContentWrapper>
        );
      })}
    </StyledContentCoverWrapper>
  );
}

const StyledContentCoverWrapper = styled.div`
  display: flex;
  //max-width: 390px;
  width: 100%;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 16px;
  gap: 10px;

  ${hideScrollbar}
`;

const StyledContentWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 16px;
`;

const StyledContentMetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.XSmall};
  ${getTextLineStyle(1)};
`;

const StyledDescription = styled.p`
  color: ${Color.Gray700};
  font-weight: ${FontWeight.Regular};
  ${Typography.XXXSmall};
  ${getTextLineStyle(2)}
  word-break: initial;
`;

const StyledMeta = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
  color: ${Color.Gray600};
`;

const StyledCoverWrapper = styled.div(
  ({ canEnlarge, size, ratio }: { size: number; canEnlarge: boolean; ratio: number }) => css`
    ${getCoverImageBorder()};
    position: relative;
    z-index: ${ZIndex.ForStackingContext};
    width: ${size}px;
    height: ${size * ratio}px;
    background-color: ${Color.White};

    cursor: ${canEnlarge ? 'pointer' : 'inherit'};

    & img {
      ${getCoverImageStyle()}
    }
  `,
);
